import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { InputText } from 'primeng/inputtext';
import { ConfirmationService } from 'primeng/api';
import { CampaignService, Campaign } from '../../core/services/campaign.service';
import { ApiError } from '../../shared/models';

@Component({
	selector: 'app-campaigns',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [FormsModule, InputText],
	templateUrl: './campaigns.html',
	styleUrl: './campaigns.scss',
})
export class CampaignsComponent implements OnInit {
	private route = inject(ActivatedRoute);
	private router = inject(Router);
	private destroyRef = inject(DestroyRef);
	protected campaignService = inject(CampaignService);
	private confirmationService = inject(ConfirmationService);

	protected companySlug = '';
	protected newName = '';
	protected creating = signal(false);
	protected createError = signal<string | null>(null);

	ngOnInit(): void {
		// React to :companySlug changes so the company switcher reloads campaigns
		// without a full component re-instantiation. URL → activeCompany sync is
		// handled centrally by CompanyUrlSyncService.
		this.route.paramMap
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(params => {
				const slug = params.get('companySlug') ?? '';
				if (slug === this.companySlug) return;
				this.companySlug = slug;
				this.campaignService.loadCampaigns(slug);
			});
	}

	protected createCampaign(): void {
		if (!this.newName.trim()) return;
		this.creating.set(true);
		this.createError.set(null);

		this.campaignService.createCampaign(this.companySlug, { name: this.newName.trim() }).subscribe({
			next: () => {
				this.creating.set(false);
				this.newName = '';
				this.campaignService.loadCampaigns(this.companySlug);
			},
			error: (err: ApiError) => {
				this.creating.set(false);
				this.createError.set(err?.error?.error ?? err?.message ?? 'Failed');
			},
		});
	}

	protected deleteCampaign(campaign: Campaign, event: MouseEvent): void {
		event.stopPropagation();
		this.confirmationService.confirm({
			header: 'Delete campaign',
			message: `Delete campaign "${campaign.name}"? This also deletes all of its projects and their media. Cannot be undone.`,
			icon: 'pi pi-exclamation-triangle',
			acceptLabel: 'Delete',
			rejectLabel: 'Cancel',
			acceptButtonProps: { severity: 'danger' },
			accept: () => {
				this.campaignService.deleteCampaign(this.companySlug, campaign.slug).subscribe({
					next: () => this.campaignService.loadCampaigns(this.companySlug),
				});
			},
		});
	}

	protected renameCampaign(campaign: Campaign, newName: string): void {
		if (!newName.trim() || newName.trim() === campaign.name) return;
		this.campaignService.editCampaign(this.companySlug, campaign.slug, newName.trim()).subscribe({
			next: () => this.campaignService.loadCampaigns(this.companySlug),
		});
	}

	protected goToCampaign(campaign: Campaign): void {
		this.router.navigate(['/', this.companySlug, 'campaigns', campaign.slug]);
	}
}
